/**
 * Pure Monte Carlo Tree Search Engine
 * "Explorer" - uses random playouts to discover good moves
 */

import { GameState, IPiece, State } from "../../types";
import { MCTSNode, MoveInfo } from "./MCTSNode";
import { Simulation } from "./Simulation";
import { cloneFullGameState } from "../../MCTS_v2/utils/MCTSUtils";

export interface MCTSResult {
  move: MoveInfo;
  visits: number;
  winRate: number;
  totalIterations: number;
  explorationStats: {
    nodesCreated: number;
    maxDepth: number;
    averageSimulationDepth: number;
  };
}

export class MCTSEngine {
  private explorationParameter: number = 1.41; // UCB1 exploration constant
  private simulator: Simulation;
  private maxIterations: number = 100000; // Increased from 5000 - let time limit control search
  private timeLimit: number = 10000;
  private useHeuristicSimulations: boolean = true;

  constructor() {
    this.simulator = new Simulation();
  }

  /**
   * Find best move using MCTS
   */
  findBestMove(gameState: GameState, maxTime: number): MCTSResult | null {
    const startTime = Date.now();
    this.timeLimit = maxTime * 0.95; // Use 95% of time

    // Isolate the AI's view from the live game state. Without this, every
    // downstream read of node.gameState aliases the user's real board, and
    // any piece flag mutated during search (isEntangled, hasMoved, etc.)
    // sticks on the live piece — making the piece unselectable for the
    // rest of the match.
    const isolatedState = cloneFullGameState(gameState);

    // Get all possible moves
    const allMoves = MCTSNode.getAllMoves(isolatedState);
    if (allMoves.length === 0) return null;

    // Create root node
    const rootNode = new MCTSNode(isolatedState, null, null, allMoves);
    const startingPlayer = isolatedState.isLightTurn;

    let iterations = 0;
    let nodesCreated = 1;
    let maxDepth = 0;
    let totalSimulationDepth = 0;

    // MCTS main loop
    while (iterations < this.maxIterations && Date.now() - startTime < this.timeLimit) {
      // Selection - traverse tree using UCB1
      let node = rootNode;
      let depth = 0;

      while (!node.isTerminal() && node.isFullyExpanded && node.children.length > 0) {
        node = node.selectBestChild(this.explorationParameter);
        depth++;
      }

      // Expansion - add new child if not terminal
      if (!node.isTerminal() && node.untriedMoves.length > 0) {
        const move = node.untriedMoves[Math.floor(Math.random() * node.untriedMoves.length)];
        const newState = this.applyMove(node.gameState, move);

        if (newState) {
          const childMoves = MCTSNode.getAllMoves(newState);
          node = node.expand(newState);
          node.untriedMoves = childMoves;
          nodesCreated++;
          depth++;
        }
      }

      maxDepth = Math.max(maxDepth, depth);

      // Simulation - play random game to terminal
      const simulationResult = this.useHeuristicSimulations
        ? this.simulator.simulateWithHeuristics(node.gameState, startingPlayer)
        : this.simulator.simulate(node.gameState, startingPlayer);

      totalSimulationDepth += depth;

      // Backpropagation - update statistics
      let current: MCTSNode | null = node;
      while (current !== null) {
        current.update(simulationResult);
        current = current.parent;
      }

      iterations++;

      // Log progress every 1000 iterations
      if (iterations % 1000 === 0) {
        const elapsed = Date.now() - startTime;
      }
    }


    // Select best move based on visit count
    if (rootNode.children.length === 0) return null;

    const bestChild = rootNode.getBestChildByVisits();
    if (!bestChild.move) return null;

    // Rebind side-effect piece references onto the LIVE board so handlers
    // that mutate `piecesToEffect[i]` directly (e.g. entangle setting
    // isEntangled, capture removing by reference) act on the real pieces
    // instead of the AI's isolated clones.
    const reboundMove = this.rebindSideEffectsToLiveState(bestChild.move, gameState);

    return {
      move: reboundMove,
      visits: bestChild.visits,
      winRate: bestChild.getWinRate(),
      totalIterations: iterations,
      explorationStats: {
        nodesCreated,
        maxDepth,
        averageSimulationDepth: totalSimulationDepth / iterations,
      },
    };
  }

  /**
   * Walk a chosen move's side effects and replace any cloned piece references
   * in `piecesToEffect` with the matching live pieces. Pieces are matched by
   * id first, then by type+color as a fallback. Also rebinds `toPiece`.
   */
  private rebindSideEffectsToLiveState(
    moveInfo: MoveInfo,
    liveState: GameState
  ): MoveInfo {
    const livePieces: IPiece[] = [];
    for (const row of liveState.board) {
      if (!row) continue;
      for (const square of row) {
        if (!square?.Pieces) continue;
        for (const p of square.Pieces) livePieces.push(p);
      }
    }

    const findLive = (clone: IPiece): IPiece | null => {
      const byId = livePieces.find((p) => p.id === clone.id);
      if (byId) return byId;
      return (
        livePieces.find(
          (p) => p.type === clone.type && p.isLight === clone.isLight
        ) ?? null
      );
    };

    const reboundSideEffects = moveInfo.move.sideEffects.map((se) => {
      if (!se.piecesToEffect || se.piecesToEffect.length === 0) return se;
      const rebound = se.piecesToEffect.map(
        (p) => findLive(p) ?? p
      );
      return { ...se, piecesToEffect: rebound };
    });

    const reboundToPiece = moveInfo.move.toPiece
      ? findLive(moveInfo.move.toPiece) ?? moveInfo.move.toPiece
      : moveInfo.move.toPiece;

    return {
      fromPosition: moveInfo.fromPosition,
      move: {
        ...moveInfo.move,
        toPiece: reboundToPiece,
        sideEffects: reboundSideEffects,
      },
    };
  }

  /**
   * Apply move to game state
   */
  private applyMove(gameState: GameState, moveInfo: MoveInfo): GameState | null {
    try {
      const newState = cloneFullGameState(gameState);

      const fromSquare = newState.board[moveInfo.fromPosition.row][moveInfo.fromPosition.col];
      const toSquare = newState.board[moveInfo.move.position.row][moveInfo.move.position.col];

      // After cloning, piece IDs change, so just take the first piece at fromPosition
      if (fromSquare.Pieces.length === 0) return null;

      const piece = fromSquare.Pieces.splice(0, 1)[0];

      // Handle captures
      if (moveInfo.move.toPiece) {
        const captureIndex = toSquare.Pieces.findIndex((p: any) => p.id === moveInfo.move.toPiece?.id);
        if (captureIndex !== -1) {
          toSquare.Pieces.splice(captureIndex, 1);
        }
      }

      toSquare.Pieces.push(piece);
      newState.isLightTurn = !newState.isLightTurn;

      // Check win conditions (race victory)
      if (piece.isKing) {
        if ((piece.isLight && moveInfo.move.position.row === 7) ||
            (!piece.isLight && moveInfo.move.position.row === 0)) {
          newState.state = State.PostGame;
          newState.lightWon = piece.isLight;
        }
      }

      return newState;
    } catch (error) {
      return null;
    }
  }

  /**
   * Inject a custom simulation strategy (e.g. WispSimulation for race-biased rollouts)
   */
  setSimulator(simulator: Simulation): void {
    this.simulator = simulator;
  }

  /**
   * Configure MCTS parameters
   */
  configure(config: {
    explorationParameter?: number;
    maxIterations?: number;
    useHeuristicSimulations?: boolean;
  }): void {
    if (config.explorationParameter !== undefined) {
      this.explorationParameter = config.explorationParameter;
    }
    if (config.maxIterations !== undefined) {
      this.maxIterations = config.maxIterations;
    }
    if (config.useHeuristicSimulations !== undefined) {
      this.useHeuristicSimulations = config.useHeuristicSimulations;
    }
  }

  /**
   * Get top N moves by visit count
   */
  getTopMoves(rootNode: MCTSNode, n: number): Array<{ move: MoveInfo; visits: number; winRate: number }> {
    return rootNode.children
      .filter((child) => child.move !== null)
      .map((child) => ({
        move: child.move!,
        visits: child.visits,
        winRate: child.getWinRate(),
      }))
      .sort((a, b) => b.visits - a.visits)
      .slice(0, n);
  }
}